'use client';

import { useState } from 'react';

import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import {
  ProductPrice,
  useProductPriceDisplay,
} from '@eliadmin/core/src/components/nt/product/product-price';
import {
  parseRating,
  RatingStars,
  ratingLabel,
} from '@eliadmin/core/src/components/nt/product/product-rating';
import { ProductTileActions } from '@eliadmin/core/src/components/nt/product/product-tile-actions';
import { AskForPriceDialog } from '@eliadmin/core/src/components/product/dialog/ask-for-price-dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProductImage, getProductUrl } from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';

/** Figma „a.chip“ on the photo: Body/XS, 8 × 6 px padding, radius 8. */
const CHIP = 'nt-body-xs rounded-[8px] px-2 py-1.5';

/** Text on an admin tag colour: the core contrast pick between white and text/primary. */
const TAG_TEXT = { light: '#FFFFFF', dark: '#0D110D' };

/** The add-to-cart row and its white fade show on hover and keyboard focus, always on touch. */
const REVEAL =
  'opacity-0 transition-opacity motion-reduce:transition-none group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100';

type ProductTileProps = {
  product: ProductDisplayData;
  /** `sizes` of the square photo in the grid that renders the tile. */
  imageSizes: string;
  className?: string;
};

/**
 * Figma „Product“ (Styleguide 20:967, hover state 46:4005): square photo with chips, name on two
 * lines, price and – over the bottom of the photo – add to cart and favourite. The whole tile links
 * to the product (stretched name link); chips are the core discount and the product tags.
 * Reusable on category pages: it takes the core card type and reads the store settings itself.
 */
export function ProductTile({ product, imageSizes, className }: ProductTileProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const price = useProductPriceDisplay(product);
  const [askForPrice, setAskForPrice] = useState(false);

  const image = getProductImage(templateSettings, product, []);
  const rating = parseRating(product.rating);

  return (
    <article className={cn('group relative flex w-full flex-col gap-4 @container', className)}>
      <div className="relative aspect-square overflow-hidden rounded-2xl border border-[#E2E5DF] bg-[#FFFFFF]">
        {image.url ? (
          <Image src={image.url} alt="" fill sizes={imageSizes} className="object-contain" />
        ) : null}
        <span
          aria-hidden="true"
          className={cn(
            'absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-[#FFFFFF00] to-[#FFFFFF]',
            REVEAL,
          )}
        />
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="nt-title-s line-clamp-2 min-h-[2lh] text-[#0D110D] transition-colors group-hover:text-[#0E6A20]">
          <Link
            href={getProductUrl(product, [])}
            prefetchOnHover
            className="after:absolute after:inset-0 after:z-[1] after:rounded-2xl focus-visible:outline-none focus-visible:after:shadow-focus"
          >
            {product.name}
          </Link>
        </h3>
        {rating ? (
          <RatingStars
            rating={rating}
            size={16}
            label={ratingLabel(t('text587'), rating, locale)}
          />
        ) : null}
        {price.priceOnRequest ? (
          <>
            <button
              type="button"
              onClick={() => setAskForPrice(true)}
              className="nt-body-l-bold relative z-[2] self-start rounded-[8px] text-[#0E6A20] underline-offset-4 hover:underline focus-visible:shadow-focus focus-visible:outline-none"
            >
              {t('text2278')}
            </button>
            <AskForPriceDialog isOpen={askForPrice} setIsOpen={setAskForPrice} product={product} />
          </>
        ) : (
          <ProductPrice product={product} />
        )}
      </div>

      <div className="absolute left-4 top-4 flex max-w-[calc(100%-32px)] flex-wrap gap-1.5">
        {price.showPromotion ? (
          <span className={cn(CHIP, 'bg-[#D92D20] text-[#FFFFFF]')}>
            {`${t('text3265')} -${price.promotionDiscountLabel}%`}
          </span>
        ) : null}
        {product.tags?.map(tag => (
          <span
            key={tag.id ?? tag.name}
            className={cn(CHIP, !tag.color && 'bg-[#00A3E0] text-[#FFFFFF]')}
            style={
              tag.color
                ? { backgroundColor: tag.color, color: getContrastYIQ(tag.color, TAG_TEXT) }
                : undefined
            }
          >
            {tag.name}
          </span>
        ))}
      </div>

      <ProductTileActions
        product={product}
        className={cn('absolute inset-x-4 top-[calc(100cqw-64px)] z-[2]', REVEAL)}
      />
    </article>
  );
}
